import { get } from 'lodash-es'
import { mapState } from 'pinia'

import PanelLoading from 'src/components/panel_loading/panel_loading.vue'
import RichContent from 'src/components/rich_content/rich_content.jsx'

import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useStatusesStore } from 'src/stores/statuses.js'
import { useStreamingStore } from 'src/stores/streaming.js'

import { fetchConversation, fetchStatus } from 'src/api/public.js'
import { WSConnectionStatus } from 'src/api/websocket.js'
import { observeHeight } from 'src/services/height_observer/height_observer.js'
import { compareIds, isNumericId } from 'src/services/ids/ids.js'

// oldest first: numeric (Mastodon) ids before text ones, each exactly
const sortById = (a, b) => {
  const idA = a.type === 'retweet' ? a.retweeted_status.id : a.id
  const idB = b.type === 'retweet' ? b.retweeted_status.id : b.id
  const numericA = isNumericId(idA)
  if (numericA !== isNumericId(idB)) return numericA ? -1 : 1
  return compareIds(idA, idB)
}

// A big thread is built in steps (every post blocks the page for a few
// milliseconds): the posts up to the chosen one and the next FIRST_STEP at
// once, the rest in steps after the browser has drawn. Only posts below
// are added later, so nothing on the screen moves.
const FIRST_STEP = 20
export const shownCount = ({ total, focusIndex, added }) =>
  Math.min(total, Math.max(focusIndex, 0) + 1 + FIRST_STEP + added)

// each step about STEP_MS of work, on any device: a slow one gets smaller
// steps, so the page stays usable while the thread fills in
const STEP_MS = 30
export const nextStepSize = (size, spent) =>
  Math.min(50, Math.max(1, Math.round((size * STEP_MS) / Math.max(spent, 0.1))))

const conversation = {
  props: {
    statusId: {
      // Main thing
      type: String,
      required: true,
    },
    collapsable: {
      // Whether conversation can be collapsed
      // i.e. when it's not a page
      type: Boolean,
      default: false,
    },
    isPage: {
      // Whether conversation is rendered as a standalone page
      // as opposed to embedded into a timeline
      type: Boolean,
      default: false,
    },
    inProfile: {
      // Whether conversation is rendered in a user profile
      // used for overriding muted status
      type: Boolean,
      default: false,
    },
    // for the server's word filters, see Status
    filterPlace: { type: String, default: null },
    profileUserId: {
      // used with inProfile, user id of the profile
      type: String,
      default: null,
    },
    virtualHidden: {
      // Whether conversation is suspended. Controls rendering of statuses
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focused: null,
      expanded: false,
      loadStatusError: null,
      unsuspendibleIds: new Set(),
      virtualHeight: 120,
      // posts added to the first step so far, and the furthest chosen post:
      // what is built stays when the focus moves up
      added: 0,
      furthestFocus: -1,
    }
  },
  created() {
    if (this.isPage) {
      this.fetchConversation()
    }
  },
  beforeUnmount() {
    this.setBody(null)
  },
  computed: {
    status() {
      return useStatusesStore().allStatuses.get(this.statusId)
    },
    streamingEnabled() {
      return (
        this.mergedConfig.useStreamingApi &&
        this.mastoUserSocketStatus === WSConnectionStatus.JOINED
      )
    },
    suspendable() {
      return this.unsuspendibleIds.size === 0
    },
    hide() {
      return this.virtualHidden && this.suspendable
    },
    originalStatusId() {
      if (this.status.retweeted_status) {
        return this.status.retweeted_status.id
      } else {
        return this.statusId
      }
    },
    conversationId() {
      return this.getConversationId(this.statusId)
    },
    conversation() {
      if (!this.status) {
        return []
      }

      if (!this.isExpanded) {
        return [this.status]
      }

      const conversation = useStatusesStore().conversations.get(
        this.conversationId,
      )

      return [...conversation.keys()]
        .map((k) => useStatusesStore().allStatuses.get(k))
        .filter((status) => status.type != 'repeat') // Old backend behavior?
        .toSorted(sortById)
    },
    isExpanded() {
      return !!(this.expanded || this.isPage)
    },
    // the chosen post's place: everything up to it is built at once
    focusIndex() {
      // nothing loaded yet (a link to a post the page does not have)
      if (this.conversation.length === 0) return -1
      const id = this.focused ?? this.originalStatusId
      return this.conversation.findIndex(
        (s) => s.id === id || s.retweeted_status?.id === id,
      )
    },
    shownConversation() {
      const count = shownCount({
        total: this.conversation.length,
        focusIndex: Math.max(this.focusIndex, this.furthestFocus),
        added: this.added,
      })
      return count < this.conversation.length
        ? this.conversation.slice(0, count)
        : this.conversation
    },
    hiddenStyle() {
      return { height: this.virtualHeight + 'px' }
    },
    maybeFocused() {
      return this.isExpanded ? this.focused : null
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
    ...mapState(useStreamingStore, {
      mastoUserSocketStatus: (state) => state.state,
    }),
  },
  components: {
    RichContent,
    PanelLoading,
  },
  watch: {
    statusId(newVal, oldVal) {
      const newConversationId = this.getConversationId(newVal)
      const oldConversationId = this.getConversationId(oldVal)
      if (
        newConversationId &&
        oldConversationId &&
        newConversationId === oldConversationId
      ) {
        this.setFocused(this.originalStatusId)
      } else {
        this.restartSteps()
        this.fetchConversation()
      }
    },
    expanded(value) {
      if (!value) return
      this.restartSteps()
      this.fetchConversation()
    },
    focusIndex: {
      handler(index) {
        this.furthestFocus = Math.max(this.furthestFocus, index)
      },
      immediate: true,
    },
    // more to show: the next step once the browser has drawn this one
    shownConversation: {
      handler(shown) {
        if (shown.length < this.conversation.length) this.nextStep()
      },
      immediate: true,
    },
  },
  methods: {
    fetchConversation() {
      if (this.status) {
        fetchConversation({
          id: this.statusId,
        }).then(({ data: { ancestors, descendants }, timestamp }) => {
          useStatusesStore().addNewStatuses({ statuses: ancestors, timestamp })
          useStatusesStore().addNewStatuses({
            statuses: descendants,
            timestamp,
          })
          this.setFocused(this.originalStatusId)
        })
      } else {
        this.loadStatusError = null
        fetchStatus({
          id: this.statusId,
        })
          .then(({ data: status }) => {
            useStatusesStore().addNewStatuses({ statuses: [status] })
            this.fetchConversation()
          })
          .catch((error) => {
            console.error(error)
            this.loadStatusError = error
          })
      }
    },
    setFocused(id) {
      if (!id) return
      this.focused = id

      if (!this.streamingEnabled) {
        useStatusesStore().fetchStatus(id)
      }

      useStatusesStore().fetchFavsAndRepeats(id)
      useStatusesStore().fetchEmojiReactions(id)
    },
    // another thread, or opened again: built in steps again
    restartSteps() {
      this.added = 0
      this.furthestFocus = -1
    },
    nextStep() {
      if (this.stepPending) return
      this.stepPending = true
      requestAnimationFrame(() =>
        setTimeout(() => {
          this.stepPending = false
          const size = this.stepSize ?? 10
          const started = performance.now()
          this.added += size
          // after the new posts are built
          this.$nextTick(() => {
            this.stepSize = nextStepSize(size, performance.now() - started)
          })
        }),
      )
    },
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    getConversationId(statusId) {
      const status = useStatusesStore().allStatuses.get(statusId)
      return get(
        status,
        'retweeted_status.statusnet_conversation_id',
        get(status, 'statusnet_conversation_id'),
      )
    },
    onStatusSuspendStateChange({ id, suspend }) {
      if (!suspend) {
        this.unsuspendibleIds.add(id)
      } else {
        this.unsuspendibleIds.delete(id)
      }
    },
    // the body's height, kept for the placeholder while it is hidden
    setBody(element) {
      if (element === this.bodyElement) return
      this.stopObservingBody?.()
      this.bodyElement = element
      this.stopObservingBody =
        element &&
        observeHeight(element, (height) => {
          // 0 while an ancestor is display: none; keep the real height
          if (height > 0) this.virtualHeight = height
        })
    },
  },
}

export default conversation
